import React, { useMemo } from "react";
import makeStyles from "@mui/styles/makeStyles";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import t from "module/translations";
import Typography from "@mui/material/Typography";
import nl2br from "toolkit/nl2br";
import clsx from "clsx";
import {
  MaintenanceTaskProcessStepCheckResult,
  MaintenanceTaskProcessStepInterface,
} from "module/maintenanceTaskProcessStep/types";
import MaintenanceTaskProcessStepMeasureFieldContainer from "module/maintenanceTaskProcessStep/enrichedComponent/measureValueField/MaintenanceTaskProcessStepMeasureFieldContainer";
import MaintenanceTaskProcessStepCheckResultFieldContainer from "module/maintenanceTaskProcessStep/enrichedComponent/checkResultField/MaintenanceTaskProcessStepCheckResultFieldContainer";
import MaintenanceTaskProcessStepActionPerformedFieldContainer from "module/maintenanceTaskProcessStep/enrichedComponent/actionPerformedField/MaintenanceTaskProcessStepActionPerformedFieldContainer";
import XLabelField from "component/labelField/XLabelField";
import MaintenanceTaskProcessStepRemarksFieldContainer from "module/maintenanceTaskProcessStep/enrichedComponent/remarksField/MaintenanceTaskProcessStepRemarksFieldContainer";
import MaintenanceTaskProcessStepAttachmentsSummaryFieldContainer from "module/maintenanceTaskProcessStepAttachment/enrichedComponent/summaryField/MaintenanceTaskProcessStepAttachmentsSummaryFieldContainer";
import ArticleTextContainer from "module/article/enrichedComponent/ArticleTextContainer";
import MyMaintenanceTaskProcessStepQuickFixContainer from "../../../maintenanceTaskProcessStepQuickFix/enrichedComponent/quickFix/MyMaintenanceTaskProcessStepQuickFixContainer";
import MyMaintenanceTaskProcessStepCorrectiveContainer from "../../../maintenanceTaskProcessStepCorrective/enrichedComponent/corrective/MyMaintenanceTaskProcessStepCorrectiveContainer";

const highlightColor = "rgb(253, 237, 237)";
const completedColor = "rgb(238, 253, 237)";
const failureColor = "rgb(255, 237, 237)";

type StyleProps = {
  isOpen: boolean;
  isHighlighted: boolean;
  isOk: boolean;
  isNotOk: boolean;
};

const useStyles = makeStyles({
  mainRow: {
    "& > *": {
      borderBottom: (props: StyleProps) => (props.isOpen ? "unset" : ""),
      background: (props: StyleProps) => {
        if (props.isOk) {
          return completedColor;
        }
        if (props.isNotOk) {
          return failureColor;
        }
        if (props.isHighlighted) {
          return highlightColor;
        }
        return "";
      },
    },
  },
  secondRow: {
    "& > *": {
      background: (props: StyleProps) => {
        if (props.isOk) {
          return completedColor;
        }
        if (props.isNotOk) {
          return failureColor;
        }
        if (props.isHighlighted) {
          return highlightColor;
        }
        return "";
      },
    },
  },
  component: {
    whiteSpace: "nowrap",
    width: "10px",
  },
  measure: {
    width: "150px",
  },
  checkResult: {
    width: "265px",
  },
  remarks: {
    width: "254px",
  },
  actionPerformed: {
    width: "254px",
  },
  measureValue: {
    "& input": {
      textAlign: "right",
    },
  },
  fix: {
    margin: "5px 0",
    display: "flex",
    justifyContent: "space-between",
  },
  visualBreak: {
    "& > *": {
      borderBottom: "10px solid #ccc",
    },
  },
  uncompletedCM: {
    fontSize: "9px",
    display: "flex",
    alignItems: "center",
  },
});

interface Props {
  processStep: MaintenanceTaskProcessStepInterface;
  isOpen: boolean;
  toggle: () => void;
  isActionPerformedAvailable: boolean;
  isCheckedResultAvailable: boolean;
  isQuickFixAvailable: boolean;
  isCorrectiveFixAvailable: boolean;
  isToolAndEquipmentAvailable: boolean;
  isHighlighted: boolean;
}

const MaintenanceTaskProcessStepListRow: React.FC<Props> = ({
  processStep,
  isOpen,
  toggle,
  isActionPerformedAvailable,
  isCheckedResultAvailable,
  isQuickFixAvailable,
  isCorrectiveFixAvailable,
  isToolAndEquipmentAvailable,
  isHighlighted,
}) => {
  const isOk =
    processStep.checkResult === MaintenanceTaskProcessStepCheckResult.good_yes;
  const isNotOk =
    processStep.checkResult === MaintenanceTaskProcessStepCheckResult.good_no;

  const classes = useStyles({ isOpen, isHighlighted, isOk, isNotOk });

  const descriptionColspan = isToolAndEquipmentAvailable ? 2 : 3;

  const componentsPartial = useMemo(() => {
    if (processStep.componentsInfo) {
      return processStep.componentsInfo.map((componentInfo) => (
        <div key={componentInfo.name}>
          {componentInfo.isHighlighted ? (
            <b>{componentInfo.name}</b>
          ) : (
            componentInfo.name
          )}
        </div>
      ));
    }
    return processStep.component.map((component) => (
      <div key={component}>{component}</div>
    ));
  }, [processStep.componentsInfo, processStep.component]);

  return (
    <>
      <TableRow
        hover
        className={clsx(classes.mainRow, {
          [classes.visualBreak]: processStep.visualBreakAfter && !isOpen,
        })}
        id={`maintenance-task-process-step-${processStep.id}`}
      >
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={toggle}>
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell align="left" colSpan={2}>
          <Typography
            dangerouslySetInnerHTML={{
              __html: nl2br(processStep.name),
            }}
          />
        </TableCell>
        <TableCell align="left" className={classes.component}>
          {componentsPartial}
        </TableCell>
        <TableCell align="left" className={classes.measure}>
          <MaintenanceTaskProcessStepMeasureFieldContainer />
        </TableCell>
        {isCheckedResultAvailable && (
          <TableCell align="left" className={classes.checkResult}>
            <div>
              <MaintenanceTaskProcessStepCheckResultFieldContainer />
            </div>
            {processStep.previousWorkOrderProcessStepUncompletedCMReportingNumber && (
              <div className={classes.uncompletedCM}>
                <WarningAmberIcon />
                {t.maintenanceTask.processStep.uncompletedCM(
                  processStep.previousWorkOrderProcessStepUncompletedCMReportingNumber
                )}
              </div>
            )}
            {(isQuickFixAvailable || isCorrectiveFixAvailable) && (
              <div className={classes.fix}>
                {isQuickFixAvailable && (
                  <MyMaintenanceTaskProcessStepQuickFixContainer />
                )}
                {isCorrectiveFixAvailable && (
                  <MyMaintenanceTaskProcessStepCorrectiveContainer />
                )}
              </div>
            )}
          </TableCell>
        )}
        {isActionPerformedAvailable && (
          <TableCell align="left" className={classes.actionPerformed}>
            <div>
              <MaintenanceTaskProcessStepActionPerformedFieldContainer />
            </div>
          </TableCell>
        )}
      </TableRow>
      {isOpen && (
        <TableRow
          className={clsx(classes.secondRow, {
            [classes.visualBreak]: processStep.visualBreakAfter && isOpen,
          })}
        >
          <TableCell
            align="left"
            colSpan={descriptionColspan}
            dangerouslySetInnerHTML={{
              __html: nl2br(processStep.description),
            }}
          />
          {isToolAndEquipmentAvailable && (
            <TableCell align="left">
              <ArticleTextContainer {...{ value: processStep.articleId }} />
            </TableCell>
          )}
          <TableCell align="left">
            <XLabelField
              {...{
                caption: t.maintenanceTask.processStepTypeText,
                label: processStep.type,
              }}
            />
          </TableCell>
          <TableCell align="left">
            <MaintenanceTaskProcessStepAttachmentsSummaryFieldContainer />
          </TableCell>
          <TableCell align="left" className={classes.checkResult}>
            <MaintenanceTaskProcessStepRemarksFieldContainer />
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default React.memo(MaintenanceTaskProcessStepListRow);
